import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import { List as Menu, SignOut as LogOut, User, Bell, MagnifyingGlass } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NAV, AUTH } from "@/constants/testIds";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-dvh min-h-screen overflow-hidden bg-[#f6f7fb]">
      {/* Desktop Sidebar */}
      <div className="hidden flex-shrink-0 md:flex">
        <Sidebar />
      </div>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-40 flex min-h-[72px] items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid={NAV.mobileMenu}
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5 text-[#2346ff]" weight="regular" />
            </Button>
            {/* Mobile logo */}
            <div className="flex items-center md:hidden">
              <img src="/schumann-logo.svg" alt="Schumann" className="w-32 h-auto" />
            </div>
            <div className="relative hidden w-[clamp(240px,30vw,420px)] sm:block">
              <MagnifyingGlass className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
              <input
                type="search"
                aria-label="Pesquisar na intranet"
                placeholder="Pesquisar documentos, pessoas e conteúdos"
                className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-[#2346ff]/40 focus:bg-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" aria-label="Notificações" className="relative h-11 w-11 rounded-xl">
              <Bell className="w-4 h-4 text-[#2346ff]" weight="regular" />
              <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full border-2 border-white bg-rose-500" />
            </Button>
            <div
              data-testid={NAV.userMenu}
              className="group flex min-h-11 cursor-pointer items-center gap-2 rounded-xl px-1.5 transition hover:bg-slate-50"
              onClick={() => navigate("/perfil")}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: "linear-gradient(135deg, #2346ff, #1d3ace)" }}>
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <User className="w-4 h-4" weight="regular" />
                )}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{user?.name?.split(" ")[0]}</p>
                <p className="text-xs text-slate-400 leading-tight capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid={AUTH.logoutButton}
              onClick={handleLogout}
              aria-label="Sair"
              className="h-11 w-11 rounded-xl text-slate-500 hover:text-red-500"
            >
              <LogOut className="w-4 h-4 text-[#2346ff]" weight="regular" />
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
